import React from "react";
import DataCard from "./DataCard";
import { Row, Col } from "reactstrap";
import { formatDistanceToNow } from "date-fns";

const formatRelativeTime = (timestamp) =>
    timestamp ? formatDistanceToNow(new Date(timestamp), { addSuffix: true }) : "--";

function getMetricSum(metricsGroup, keys) {
  const values = keys
    .map((key) => metricsGroup?.[key]?.value)
    .filter((v) => v != null); // keep only defined/non-null values

  // none of the keys had values
  if (values.length === 0) {
    return "N/A";
  }

  return values.reduce((sum, v) => sum + v, 0);
}

const formatJson = (val) => {
  if (val === null || val === undefined) return String(val);
  if (typeof val !== "object") return JSON.stringify(val);
  if (Array.isArray(val)) return JSON.stringify(val);

  return Object.entries(val)
    .map(([k, v]) => `${k}:\n${JSON.stringify(v)}`)
    .join("\n");
};

function InfoCard({ data, fields, entity, metrics = {}, solarForecasts = {}, deviceHealthCheck = {}, siteAlerts = {} }) {

    const alertLevelAccent = (lvl) => ({
        notification: "#A3BAC3",
        warn: "#FBB13C",
        alert: "#EC465D",
        critical: "#89023E",
    }[lvl] || "#A3BAC3");

    return (
        <div
            style={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                padding: "8px",
                margin: "4px 0",
                whiteSpace: "normal",
                overflowWrap: "break-word",
            }}
        >
            {fields.map((field) => {
                const key = typeof field === "object" ? field.name : field;
                const label = typeof field === "object" ? field.label : field;
                let fieldValue;
                if (typeof field === "object" && typeof field.transform === "function") {
                    fieldValue = field.transform(data[key]);
                } else {
                    fieldValue = data[key.toLowerCase()];
                    fieldValue = JSON.stringify(fieldValue);
                }

                return (
                    <div 
                        key={key} 
                        style={{ 
                            marginBottom: "4px",
                             wordBreak: "break-word" 
                             }}>
                            <strong>{label}:</strong> {fieldValue}
                    </div>
                );
            })}

      {entity === "site" && (
        <>
          <Row>
            <Col sm={4} className="d-flex flex-column">
              <DataCard
                label="Load (W)"
                value={getMetricSum(metrics.A12, ['Q01', 'Q06', 'Q07'])}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A12?.Q01?.time)}
              />
              <DataCard
                label="Battery SoC (%)"
                value={metrics.A14?.Q04?.value}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A14?.Q04?.time)}
              />
            </Col>
            <Col sm={4} className="d-flex flex-column">
              <DataCard
                label="PV (W)"
                value={metrics.A13?.Q01?.value}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A13?.Q01?.time)}
              />
              <DataCard
                label="Battery Power (W)"
                value={metrics.A14?.Q01?.value}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A14?.Q01?.time)}
              />
            </Col>
            <Col sm={4} className="d-flex flex-column">
              <DataCard
                label="Grid (W)"
                value={getMetricSum(metrics.A01, ['Q01', 'Q06', 'Q07'])}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A01?.Q01?.time)}
              />
              <DataCard
                label="Battery Set Point (W)"
                value={metrics.A15?.Q05?.value}
                defaultValue="N/A"
                subLabel={formatRelativeTime(metrics.A15?.Q05?.time)}
              />
            </Col>
          </Row>

          <Row>
            <Col sm={4} className="d-flex flex-column">
              <DataCard
                label="PV forecast current hour (Wh)"
                value={solarForecasts[0]?.forecast}
                defaultValue="N/A"
                subLabel={formatRelativeTime(solarForecasts[0]?.time)}
              />
            </Col>

            <Col sm={4} className="d-flex flex-column">
              <DataCard
                label="Latest Health Check"
                defaultValue="N/A"
                subLabel={
                  Array.isArray(deviceHealthCheck) && deviceHealthCheck[0]?.time
                    ? formatRelativeTime(deviceHealthCheck[0].time)
                    : ""
                }
                contentStyle={{
                  maxHeight: "180px", // adjust based on your design
                }}
                value={
                  Array.isArray(deviceHealthCheck) && deviceHealthCheck[0]?.system_data ? (
                    <div>
                      <ul style={{ paddingLeft: "1rem" }}>
                        <li><strong>Edge version:</strong> {deviceHealthCheck[0].system_data.version}</li>
                        <li><strong>Local IP:</strong> {deviceHealthCheck[0].system_data.local_ip}</li>
                        <li><strong>Inverter Type:</strong> {deviceHealthCheck[0].system_data.inverter_type}</li>
                        <li><strong>Serial Number:</strong> {deviceHealthCheck[0].system_data.serial_number}</li>
                        <li><strong>Steering Type:</strong> {deviceHealthCheck[0].system_data.steering_type}</li>
                      </ul>

                      {deviceHealthCheck[0].system_data.cpu_load?.cpu_load_percent !== undefined && (
                        <p><strong>CPU Load:</strong> {deviceHealthCheck[0].system_data.cpu_load.cpu_load_percent}%</p>
                      )}

                      {deviceHealthCheck[0].system_data.memory && (
                        <div>
                          <strong>Memory:</strong>
                          <ul style={{ paddingLeft: "1rem" }}>
                            <li><strong>Used:</strong> {deviceHealthCheck[0].system_data.memory.memory_used}</li>
                            <li><strong>Total:</strong> {deviceHealthCheck[0].system_data.memory.memory_total}</li>
                            <li><strong>Available:</strong> {deviceHealthCheck[0].system_data.memory.memory_available}</li>
                            <li><strong>Usage:</strong> {deviceHealthCheck[0].system_data.memory.memory_percent}%</li>
                          </ul>
                        </div>
                      )}

                      {Array.isArray(deviceHealthCheck[0].system_data.disks) && (
                        <div>
                          <strong>Disks:</strong>
                          <ul style={{ paddingLeft: "1rem" }}>
                            {deviceHealthCheck[0].system_data.disks.map((disk, index) => (
                              <li key={index}>
                                <strong>{disk.mountpoint}</strong> ({disk.device}): {disk.used} used / {disk.total} total ({disk.percent} used)
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : "N/A"
                }
              />
            </Col>
          </Row>

          <Row>
            <Col sm={12} className="d-flex flex-column">
              <DataCard
                label="Alerts"
                defaultValue="No alerts found."
                contentStyle={{
                  paddingRight: 8,
                  maxHeight: "clamp(240px, 42vh, 560px)"
                }}
                value={
                  Array.isArray(siteAlerts) && siteAlerts.length > 0 ? (
                    <ul style={{ paddingLeft: 0, margin: 0 }}>
                      {siteAlerts.map((a, idx) => (
                        <li
                          key={idx}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "4px 1fr",
                            gap: 12,
                            padding: "10px 0"
                          }}
                        >
                          <div
                            aria-hidden
                            style={{
                              background: alertLevelAccent(a.level),
                              borderRadius: 2,
                            }}
                          />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                              <strong>{a.type || "alert"}</strong>
                              <span style={{ fontSize: 12, color: "#6b7280" }}>
                                {a.time ? formatRelativeTime(a.time) : ""}
                              </span>
                            </div>

                            {a.comment && (
                              <div>
                                {a.comment}
                              </div>
                            )}

                            {a.data && (
                              <div>
                                <details>
                                  <summary style={{ cursor: "pointer", fontSize: 12, color: "#2C495E" }}>
                                    Details
                                  </summary>
                                  <pre
                                    style={{
                                      padding: 8,
                                      border: "1px solid #EEE",
                                      borderRadius: 4,
                                      fontSize: 12,
                                      whiteSpace: "pre-wrap",
                                    }}
                                  >
                                    {formatJson(a.data)}
                                  </pre>
                                </details>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : null
                }
              />
            </Col>
          </Row>


        </>
      )}
    </div>
  );
}

export default InfoCard;